// ─────────────────────────────────────────────────────────────
// StockReportModal.jsx — Report 3 launcher
// Stock is a point-in-time position, so there is no date range —
// just which sections to include.
// ─────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import axios from 'axios';
import WindowModal from '../../components/WindowModal';
import PrintButton from '../../print/PrintButton';
import { stockReportConfig } from './stockReport.config';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  text: '#1a2332',
  textSoft: '#64748b',
  textMuted: '#94a3b8',
  border: '#e2e8f0',
};

const StockReportModal = ({ onClose }) => {
  const [includeJumbo, setIncludeJumbo] = useState(true);
  const [includeCores, setIncludeCores] = useState(true);
  const [includeFinished, setIncludeFinished] = useState(true);
  const [error, setError] = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const buildConfig = async () => {
    setError('');

    if (!includeJumbo && !includeCores && !includeFinished) {
      setError('Pick at least one section.');
      return null;
    }

    // Only fetch what is being printed
    const [r, b, s] = await Promise.all([
      includeJumbo
        ? axios.get(`${API}/tape/jumbo-rolls?status=active`, { headers })
        : Promise.resolve(null),
      includeCores
        ? axios.get(`${API}/tape/core-batches?available=true`, { headers })
        : Promise.resolve(null),
      includeFinished
        ? axios.get(`${API}/tape/production-runs/stock`, { headers })
        : Promise.resolve(null),
    ]);

    const rolls = r ? r.data.data || [] : [];
    const batches = b ? b.data.data || [] : [];
    // This endpoint wraps its payload: data: { stock, summary }
    const stock = s ? s.data.data?.stock || [] : [];

    if (rolls.length === 0 && batches.length === 0 && stock.length === 0) {
      setError('There is no stock to report.');
      return null;
    }

    const cfg = stockReportConfig({ rolls, batches, stock });

    // Drop unticked sections, keeping the value box honest
    if (!includeJumbo || !includeCores || !includeFinished) {
      const keep = [];
      if (includeJumbo) keep.push('Jumbo Rolls in Stock');
      if (includeCores) keep.push('Cores in Hand');
      if (includeFinished) keep.push('Finished Stock');
      cfg.sections = cfg.sections.filter((sec) => keep.includes(sec.title));
    }

    return cfg;
  };

  const check = (label, value, setter, hint) => (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: '10px',
      padding: '12px 14px', border: `1px solid ${C.border}`,
      borderRadius: '8px', cursor: 'pointer',
    }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => setter(e.target.checked)}
        style={{ marginTop: '3px', width: '16px', height: '16px', cursor: 'pointer' }}
      />
      <span>
        <span style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: C.text }}>
          {label}
        </span>
        <span style={{ display: 'block', fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
          {hint}
        </span>
      </span>
    </label>
  );

  return (
    <WindowModal
      id="stock-report"
      title="Stock Report"
      subtitle="Current stock position across the whole chain."
      onClose={onClose}
      width="600px"
    >
      <div style={{ padding: '22px 24px' }}>
        <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
          {check('Jumbo rolls', includeJumbo, setIncludeJumbo,
            'Active rolls with remaining meters and value at landed cost.')}
          {check('Cores in hand', includeCores, setIncludeCores,
            'Unused cores, grouped by tape type and core specification.')}
          {check('Finished stock', includeFinished, setIncludeFinished,
            'Produced rolls at moving-average cost, open and customer-locked.')}
        </div>

        {error && (
          <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <PrintButton
            label="Download PDF"
            filename={`Stock-Report-${new Date().toISOString().split('T')[0]}`}
            getConfig={buildConfig}
          />
          <PrintButton label="Print" mode="print" getConfig={buildConfig} />
          <span style={{ fontSize: '12px', color: C.textMuted, marginLeft: 'auto' }}>
            Position as at today
          </span>
        </div>
      </div>
    </WindowModal>
  );
};

export default StockReportModal;